import { Fragment } from "preact";
import type { ComponentChildren } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { reviewIdsForHunk, reviewSelection } from "../lib/review-state.js";
import { githubLineUrl } from "../lib/github-links.js";
import { safePlainText } from "../lib/safe-text.js";
import {
  clearComparisonFocus,
  clearMoveFocus,
  clearWordFocus,
  focusComparison,
  focusMove,
  focusWord,
} from "../lib/focus.js";
import { useApp } from "../app-context.js";
import {
  fileCounts,
  fileIcon,
  layoutSplitDiffRows,
  moveColor,
  moveReviewId,
  securityWarningCount,
  visualColumns,
} from "../lib/view-model.js";
import { SafeText } from "./safe-text.js";
import { darkSyntaxColor, lightSyntaxColor } from "../lib/syntax-color.js";
import type {
  Analysis,
  ComparisonIndex,
  DiffLine as DiffLineModel,
  DiffSide,
  FileDiff,
  FollowMove,
  Hunk as HunkModel,
  SyntaxSegment,
  TextSecurity,
  TextSegment,
} from "../types.js";

interface Counts {
  added: number;
  deleted: number;
}

function FileCount({ counts }: { counts: Counts }) {
  return (
    <div class="file-counts">
      <span>+{counts.added}</span><i>−{counts.deleted}</i>
    </div>
  );
}

function RenamePath({ counts, file, side }: { counts: Counts; file: FileDiff; side: DiffSide }) {
  const oldSide = side === "old";
  const path = oldSide ? file.oldPath : file.newPath;
  const segments = oldSide ? file.rename!.oldPath : file.rename!.newPath;
  return (
    <div class={`rename-path rename-path-${side}`}>
      <span class="rename-label">{oldSide ? "from" : "to"}</span>
      <code title={safePlainText(path)}>
        {segments.map((segment, index) => segment.changed ? (
          <mark class="path-change" key={index}><SafeText text={segment.text} /></mark>
        ) : (
          <SafeText text={segment.text} key={index} />
        ))}
      </code>
      {!oldSide && <FileCount counts={counts} />}
    </div>
  );
}

function FileHeader({ file }: { file: FileDiff }) {
  const counts = fileCounts(file);
  if (file.status === "renamed" && file.rename) {
    return (
      <header class="rename-header">
        <RenamePath counts={counts} file={file} side="old" />
        <RenamePath counts={counts} file={file} side="new" />
      </header>
    );
  }
  return (
    <header>
      <div class="file-title">
        <span>{fileIcon(file.displayPath)}</span>
        <code title={safePlainText(file.displayPath)}><SafeText text={file.displayPath} /></code>
        <b class={`file-status status-${file.status}`}>{file.status}</b>
      </div>
      <FileCount counts={counts} />
    </header>
  );
}

function UnicodeWarning({ security }: { security: TextSecurity }) {
  const details = ([
    [security.bidiControls, "bidirectional control"],
    [security.invisibleCharacters, "invisible or non-ASCII spacing character"],
    [security.controlCharacters, "control character"],
  ] satisfies Array<[number, string]>)
    .filter(([count]) => count > 0)
    .map(([count, label]) => `${count} ${label}${count === 1 ? "" : "s"}`)
    .join(", ");
  return (
    <div class="unicode-warning" role="alert">
      <span>⚠</span>
      <span>
        <strong>Potentially deceptive Unicode detected.</strong>{" "}
        {details}. Unsafe characters are replaced with visible labels below.{" "}
        <a href="https://github.co/hiddenchars" target="_blank" rel="noreferrer">Learn more</a>
      </span>
    </div>
  );
}

function ReviewButton({ reviewId, side }: { reviewId: string; side: DiffSide }) {
  const { reviewed, toggleReviewed } = useApp();
  const isReviewed = reviewed.has(reviewId);
  return (
    <button
      type="button"
      class={`review-change-button review-${side}${isReviewed ? " is-reviewed" : ""}`}
      data-review-control={reviewId}
      aria-label={isReviewed ? "Mark this change as not reviewed" : "Mark this change as reviewed"}
      onClick={(event) => {
        event.stopPropagation();
        toggleReviewed(reviewId);
      }}
    >
      {isReviewed ? "↶ Unreview" : "✓ Review"}
    </button>
  );
}

function HunkReviewButton({ reviewIds }: { reviewIds: string[] }) {
  const { reviewed, toggleReviewed } = useApp();
  const selection = reviewSelection(reviewIds, reviewed);
  const partial = selection.reviewedCount > 0 && !selection.allReviewed;
  return (
    <button
      type="button"
      class={`hunk-review-button${selection.allReviewed ? " is-reviewed" : ""}${partial ? " is-partial" : ""}`}
      title={partial ? `${selection.reviewedCount} of ${selection.ids.length} changes reviewed` : ""}
      aria-label={selection.allReviewed ? "Mark every change in this hunk as not reviewed" : "Mark every change in this hunk as reviewed"}
      onClick={() => toggleReviewed(reviewIds)}
    >
      {selection.allReviewed ? "↶ Unreview hunk" : "✓ Review hunk"}
    </button>
  );
}

function ReviewPlaceholder({ reviewId }: { reviewId: string }) {
  const { toggleReviewed } = useApp();
  return (
    <div class="review-placeholder" data-review-placeholder={reviewId}>
      <button type="button" onClick={() => toggleReviewed(reviewId)}>
        {reviewId.startsWith("move:") ? "✓ Moved code reviewed · Show" : "✓ Change reviewed · Show"}
      </button>
    </div>
  );
}

function HunkPlaceholder({ reviewIds }: { reviewIds: string[] }) {
  const { toggleReviewed } = useApp();
  return (
    <div class="hunk-review-placeholder">
      <button type="button" onClick={() => toggleReviewed(reviewIds)}>✓ Hunk reviewed · Show hunk</button>
    </div>
  );
}

function StyledText({ segment }: { segment: SyntaxSegment }) {
  if (!segment.color && !segment.bold && !segment.italic) {
    return <SafeText text={segment.text} />;
  }
  return (
    <span
      class="syntax-token"
      style={{
        "--syntax-color-light": segment.color ? lightSyntaxColor(segment.color) : undefined,
        "--syntax-color-dark": segment.color ? darkSyntaxColor(segment.color) : undefined,
        fontStyle: segment.italic ? "italic" : undefined,
        fontWeight: segment.bold ? "600" : undefined,
      }}
    >
      <SafeText text={segment.text} />
    </span>
  );
}

export function Segments({
  comparisonId,
  segments,
}: {
  comparisonId: string | null;
  segments: TextSegment[];
}) {
  if (!segments.length) return " ";
  const nodes: ComponentChildren[] = [];
  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index];
    if (!segment.changed) {
      nodes.push(<StyledText segment={segment} key={index} />);
      continue;
    }
    const start = index;
    const group = segment.changeGroup;
    const grouped: ComponentChildren[] = [];
    while (
      index < segments.length &&
      segments[index].changed &&
      segments[index].changeGroup === group
    ) {
      grouped.push(<StyledText segment={segments[index]} key={index} />);
      index += 1;
    }
    index -= 1;
    const wordId = comparisonId && group != null ? `${comparisonId}:${group}` : null;
    nodes.push(
      <mark
        class="word-change"
        data-word-comparison-id={wordId || undefined}
        key={`change-${start}`}
        onMouseEnter={wordId ? (event) => focusWord(wordId, event.currentTarget) : undefined}
        onMouseLeave={wordId ? () => clearWordFocus(wordId) : undefined}
      >
        {grouped}
      </mark>,
    );
  }
  return nodes;
}

function Syntax({ fallback, segments }: { fallback: string; segments: SyntaxSegment[] }) {
  if (!segments.length) return <SafeText text={fallback} />;
  return segments.map((segment, index) => <StyledText segment={segment} key={index} />);
}

function githubPathForFile(file: FileDiff, side: DiffSide): string {
  const explicitRename = file.status === "renamed" && file.rename && !file.rename.inferred;
  return explicitRename ? file.newPath : side === "old" ? file.oldPath : file.newPath;
}

function githubAnchorPath(analysis: Analysis, path: string, side: DiffSide): string {
  if (side !== "old") return path;
  const rename = analysis.files.find((file) =>
    file.oldPath === path && file.status === "renamed" && file.rename && !file.rename.inferred,
  );
  return rename?.newPath ?? path;
}

export function useGithubLine(
  path: string,
  side: DiffSide,
  lineNumber: number | null,
  rangeKey: string,
) {
  const {
    analysis,
    commentDrag,
    commentSelection,
    githubPathHashes,
    githubPullUrl,
    setCommentSelection,
  } = useApp();
  if (!githubPullUrl || lineNumber == null) return null;
  const anchorPath = githubAnchorPath(analysis, path, side);
  const pathHash = githubPathHashes.get(anchorPath);
  if (!pathHash) return null;
  const matches = commentSelection &&
    commentSelection.path === anchorPath &&
    commentSelection.side === side &&
    commentSelection.rangeKey === rangeKey;
  const selected = matches && lineNumber >= commentSelection.start && lineNumber <= commentSelection.end;
  const start = selected ? commentSelection.start : lineNumber;
  const end = selected ? commentSelection.end : lineNumber;
  const count = end - start + 1;

  const select = (anchor: number, line: number) => setCommentSelection({
    anchor,
    end: Math.max(anchor, line),
    path: anchorPath,
    rangeKey,
    side,
    start: Math.min(anchor, line),
  });

  return {
    anchorPath,
    count,
    endSelected: selected && lineNumber === commentSelection.end,
    href: githubLineUrl(githubPullUrl, pathHash, side, start, end),
    onNumberClick: (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
    },
    onPointerDown: (event: PointerEvent) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      const extendsSelection = event.shiftKey && matches;
      const anchor = extendsSelection ? commentSelection.anchor : lineNumber;
      commentDrag.current = { anchor, path: anchorPath, rangeKey, side };
      select(anchor, lineNumber);
    },
    onPointerEnter: () => {
      const drag = commentDrag.current;
      if (!drag || drag.path !== anchorPath || drag.side !== side || drag.rangeKey !== rangeKey) return;
      select(drag.anchor, lineNumber);
    },
    selected,
    startSelected: selected && lineNumber === commentSelection.start,
  };
}

interface LineCellProps {
  file: FileDiff;
  githubRangeKey: string;
  line: DiffLineModel;
  onFollowMove: FollowMove;
  part: "code" | "gutter";
  side: DiffSide;
}

function LineCell({ file, githubRangeKey, line, onFollowMove, part, side }: LineCellProps) {
  const { comparisons } = useApp();
  const isMoveSide =
    (side === "old" && line.moveRole === "source") ||
    (side === "new" && line.moveRole === "target");
  const lineNumber = side === "old" ? line.oldNumber : line.newNumber;
  const comparisonKey = `${line.moveId}:${line.moveRole}:${lineNumber}`;
  const comparisonId = isMoveSide ? comparisons.lineComparisons.get(comparisonKey) : null;
  const editComparisonId = !isMoveSide && line.editId != null ? `edit:${line.editId}` : null;
  const effectiveComparison = comparisonId || editComparisonId;
  const path = githubPathForFile(file, side);
  const github = useGithubLine(path, side, lineNumber, githubRangeKey);
  const syntax = side === "old" ? line.oldSyntax : line.newSyntax;
  const moveSegments = isMoveSide && line.moveId ? comparisons.wordChanges.get(comparisonKey) : null;
  const segments = moveSegments || (editComparisonId && line.editSegments.length ? line.editSegments : null);

  const focus = (origin: HTMLElement) => {
    if (line.moveId && isMoveSide) focusMove(Number(line.moveId));
    if (effectiveComparison) focusComparison(effectiveComparison, origin);
  };
  const blur = () => {
    if (line.moveId && isMoveSide) clearMoveFocus();
    if (effectiveComparison) clearComparisonFocus(effectiveComparison);
  };
  const interactive = isMoveSide || Boolean(editComparisonId);
  const githubClasses = github
    ? `${github.selected ? " comment-range-selected" : ""}${github.startSelected ? " comment-range-start" : ""}${github.endSelected ? " comment-range-end" : ""}`
    : "";

  return (
    <div
      class={`line-cell ${side} ${part}${isMoveSide ? ` move-${line.moveRole}` : ""}${editComparisonId ? " line-edit" : ""}${githubClasses}`}
      id={part === "code" && isMoveSide ? line.id : undefined}
      data-move-id={isMoveSide ? line.moveId ?? undefined : undefined}
      data-comparison-id={effectiveComparison || undefined}
      data-github-path={github?.anchorPath}
      data-github-side={github ? side : undefined}
      data-github-line={github ? lineNumber ?? undefined : undefined}
      data-github-range={github ? githubRangeKey : undefined}
      style={line.moveId ? { "--move-color": isMoveSide ? moveColor(line.moveId) : "#52769a" } : editComparisonId ? { "--move-color": "#52769a" } : undefined}
      tabIndex={part === "code" && interactive ? 0 : undefined}
      onMouseEnter={interactive ? (event) => focus(event.currentTarget) : undefined}
      onMouseLeave={interactive ? blur : undefined}
      onFocus={interactive ? (event) => focus(event.currentTarget) : undefined}
      onBlur={interactive ? blur : undefined}
      onClick={isMoveSide ? () => onFollowMove(line.moveId!, line.moveRole === "source" ? "target" : "source") : undefined}
      onKeyDown={isMoveSide ? (event) => {
        if (event.key === "Enter") onFollowMove(line.moveId!, line.moveRole === "source" ? "target" : "source");
      } : undefined}
    >
      {part === "gutter" ? (
        <>
          <span
            class={`line-number${github ? " comment-select-number" : ""}`}
            aria-label={github ? `Select ${side} line ${lineNumber} for a GitHub comment` : undefined}
            onPointerDown={github?.onPointerDown}
            onPointerEnter={github?.onPointerEnter}
            onClick={github?.onNumberClick}
          >
            {lineNumber ?? ""}
          </span>
          <span class="line-marker">{line.kind === "deletion" ? "−" : line.kind === "addition" ? "+" : " "}</span>
        </>
      ) : (
        <code>
          <span class="code-content">
            {segments
              ? <Segments comparisonId={effectiveComparison} segments={segments} />
              : <Syntax fallback={line.text || " "} segments={syntax} />}
          </span>
        </code>
      )}
      {part === "code" && github && (
        <a
          class="comment-line-button"
          href={github.href}
          data-label={github.count > 1 ? `Comment ${github.count} lines` : "Comment"}
          target="_blank"
          rel="noreferrer"
          aria-label={github.count > 1 ? `Open ${github.count} selected lines on GitHub to comment` : `Open ${side} line ${lineNumber} on GitHub to comment`}
          onClick={(event) => event.stopPropagation()}
        />
      )}
    </div>
  );
}

function EmptyCell({ part, side }: { part: "code" | "gutter"; side: DiffSide }) {
  return <div class={`line-cell ${side} ${part} empty`} />;
}

interface PaneDiffLineProps extends Omit<LineCellProps, "side"> {
  alignedEdit: boolean;
  hidden: boolean;
  newLine: DiffLineModel | null;
  oldLine: DiffLineModel | null;
  reviewId: string | null;
  part: "code" | "gutter";
  side: DiffSide;
  startsGroup: boolean;
}

function PaneDiffLine({
  alignedEdit,
  file,
  githubRangeKey,
  hidden,
  line,
  newLine,
  oldLine,
  onFollowMove,
  reviewId,
  part,
  side,
  startsGroup,
}: PaneDiffLineProps) {
  const { reviewed, showReviewed } = useApp();
  const reviewedClass = reviewId && reviewed.has(reviewId) && showReviewed
    ? " reviewed-visible"
    : "";
  const lineClasses =
    `${oldLine?.kind === "deletion" ? " line-deletion" : ""}` +
    `${newLine?.kind === "addition" ? " line-addition" : ""}` +
    `${alignedEdit ? " line-edit-aligned" : ""}`;
  return (
    <div
      class={`pane-diff-line${lineClasses}${reviewedClass}`}
      data-review-id={reviewId || undefined}
      data-move-id={line.moveId || undefined}
      style={line.moveId ? { "--move-color": moveColor(line.moveId) } : undefined}
      hidden={hidden}
    >
      {oldLine || newLine ? (
        side === "old"
          ? oldLine
            ? <LineCell file={file} githubRangeKey={githubRangeKey} line={oldLine} onFollowMove={onFollowMove} part={part} side={side} />
            : <EmptyCell part={part} side={side} />
          : newLine
            ? <LineCell file={file} githubRangeKey={githubRangeKey} line={newLine} onFollowMove={onFollowMove} part={part} side={side} />
            : <EmptyCell part={part} side={side} />
      ) : (
        <div class="meta-cell">{part === "code" && side === "new" && <SafeText text={line.text} />}</div>
      )}
      {part === "code" && startsGroup && reviewId && side === (oldLine ? "old" : "new") && (
        <ReviewButton reviewId={reviewId} side={side} />
      )}
    </div>
  );
}

function MoveSpaces({ line, position }: { line: DiffLineModel; position: "before" | "after" }) {
  const { analysis, reviewed, showReviewed } = useApp();
  return analysis.moves.flatMap((move) => (["source", "target"] as const).map((role) => {
    const ids = move[role].lineIds;
    const matches = position === "before" ? ids[0] === line.id : ids.at(-1) === line.id;
    if (!matches || (reviewed.has(moveReviewId(move.id)) && !showReviewed)) return null;
    if (position === "before") {
      return (
        <div
          class={`move-preview-space move-preview-space-${role} header-space`}
          data-move-preview-space={move.id}
          style={{ "--move-color": moveColor(move.id) }}
          key={`${move.id}:${role}:header`}
        />
      );
    }
    const extraLines = Math.max(0, move.comparison.length - ids.length);
    return extraLines ? (
      <div
        class={`move-preview-space move-preview-space-${role} body-space`}
        data-move-preview-space={move.id}
        style={{ "--move-color": moveColor(move.id), height: `${extraLines * 24}px` }}
        key={`${move.id}:${role}:body`}
      />
    ) : null;
  }));
}

function moveSpacesHeight(
  analysis: Analysis,
  reviewed: Set<string>,
  showReviewed: boolean,
  line: DiffLineModel,
  position: "before" | "after",
): number {
  return analysis.moves.reduce((height, move) => height + (["source", "target"] as const)
    .reduce((roleHeight, role) => {
      const ids = move[role].lineIds;
      const matches = position === "before" ? ids[0] === line.id : ids.at(-1) === line.id;
      if (!matches || (reviewed.has(moveReviewId(move.id)) && !showReviewed)) return roleHeight;
      if (position === "before") return roleHeight + 28;
      return roleHeight + Math.max(0, move.comparison.length - ids.length) * 24;
    }, 0), 0);
}

function useVirtualRows(heights: number[], enabled: boolean) {
  const containerRef = useRef<HTMLDivElement>(null);
  const offsets = useMemo(() => {
    const values = [0];
    heights.forEach((height) => values.push(values.at(-1)! + height));
    return values;
  }, [heights]);
  const [range, setRange] = useState(() => ({
    end: enabled ? Math.min(heights.length, 160) : heights.length,
    start: 0,
  }));

  useEffect(() => {
    if (!enabled) {
      setRange((current) => current.start === 0 && current.end === heights.length
        ? current
        : { end: heights.length, start: 0 });
      return;
    }
    let frame = 0;
    const totalHeight = offsets.at(-1)!;
    const rowAtOffset = (offset: number) => {
      let low = 0;
      let high = heights.length;
      while (low < high) {
        const middle = Math.floor((low + high) / 2);
        if (offsets[middle + 1] <= offset) low = middle + 1;
        else high = middle;
      }
      return low;
    };
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const container = containerRef.current;
        if (!container) return;
        const box = container.getBoundingClientRect();
        const overscan = 800;
        const firstOffset = Math.max(0, -box.top - overscan);
        const lastOffset = Math.min(totalHeight, window.innerHeight - box.top + overscan);
        const chunk = 32;
        const start = Math.max(0, Math.floor(rowAtOffset(firstOffset) / chunk) * chunk);
        const end = Math.min(
          heights.length,
          Math.ceil((rowAtOffset(Math.max(firstOffset, lastOffset)) + 1) / chunk) * chunk,
        );
        setRange((current) => current.start === start && current.end === end ? current : { end, start });
      });
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [enabled, heights.length, offsets]);

  const start = enabled ? Math.min(range.start, heights.length) : 0;
  const end = enabled ? Math.max(start, Math.min(range.end, heights.length)) : heights.length;

  return {
    containerRef,
    end,
    enabled,
    offsets,
    start,
    top: offsets[start],
    total: offsets.at(-1)!,
  };
}

function MoveCollapsePlaceholder({ count, moveId }: { count: number; moveId: number }) {
  const { setHideUnchangedMoves } = useApp();
  return (
    <button
      type="button"
      class="move-collapse-placeholder"
      style={{ "--move-color": moveColor(moveId) }}
      onClick={() => setHideUnchangedMoves(false)}
    >
      <span>⋯</span>
      <b>{count} unchanged moved {count === 1 ? "line" : "lines"} hidden</b>
      <em>Show all</em>
    </button>
  );
}

type SplitRow = ReturnType<typeof layoutSplitDiffRows>[number];

interface PreparedRow {
  collapsedCount: number;
  height: number;
  hidden: boolean;
  lineOffset: number;
  moveCollapsed: boolean;
  reviewHidden: boolean;
  reviewId: string | null;
  row: SplitRow;
  skipCollapsed: boolean;
  startsGroup: boolean;
}

interface PreparedHunk {
  collapsed: boolean;
  githubRangeKey: string;
  heights: number[];
  hunkReviewIds: string[];
  moveEndpointIds: Set<string>;
  rowStates: PreparedRow[];
  virtualize: boolean;
}

function prepareHunk(
  analysis: Analysis,
  comparisons: ComparisonIndex,
  fileIndex: number,
  hideUnchangedMoves: boolean,
  hunk: HunkModel,
  hunkIndex: number,
  moveEndpointIds: Set<string>,
  reviewed: Set<string>,
  showReviewed: boolean,
): PreparedHunk {
  const lineReviewIds = reviewIdsForHunk(hunk.lines, fileIndex, hunkIndex) as Array<string | null>;
  const hunkReviewIds = reviewSelection(lineReviewIds, reviewed).ids as string[];
  const collapsed = reviewSelection(hunkReviewIds, reviewed).allReviewed && !showReviewed;
  const rows = layoutSplitDiffRows(hunk.lines);
  const lineIndexes = new Map(hunk.lines.map((line, index) => [line, index]));
  const rowStates = rows.map((row): PreparedRow => {
    const lineIndex = lineIndexes.get(row.line)!;
    const reviewId = lineReviewIds[lineIndex];
    const reviewHidden = reviewId != null && reviewed.has(reviewId) && !showReviewed;
    const moveCollapsed = !collapsed && !reviewHidden && hideUnchangedMoves && comparisons.collapsedLineIds.has(row.line.id);
    const previous = hunk.lines[lineIndex - 1];
    const skipCollapsed = moveCollapsed && previous?.moveId === row.line.moveId && comparisons.collapsedLineIds.has(previous.id);
    let collapsedCount = 0;
    if (moveCollapsed && !skipCollapsed) {
      collapsedCount = 1;
      while (
        hunk.lines[lineIndex + collapsedCount]?.moveId === row.line.moveId &&
        comparisons.collapsedLineIds.has(hunk.lines[lineIndex + collapsedCount].id)
      ) collapsedCount += 1;
    }
    const startsGroup = reviewId != null && lineReviewIds[lineIndex - 1] !== reviewId;
    const hidden = collapsed || reviewHidden;
    const lineOffset = moveSpacesHeight(analysis, reviewed, showReviewed, row.line, "before") +
      (startsGroup && reviewHidden ? 24 : 0);
    const height = collapsed || skipCollapsed
      ? 0
      : moveCollapsed
        ? 28
        : lineOffset +
          (hidden ? 0 : 24) +
          moveSpacesHeight(analysis, reviewed, showReviewed, row.line, "after");
    return {
      collapsedCount,
      height,
      hidden,
      lineOffset,
      moveCollapsed,
      reviewHidden,
      reviewId,
      row,
      skipCollapsed,
      startsGroup,
    };
  });
  return {
    collapsed,
    githubRangeKey: `${fileIndex}:${hunkIndex}`,
    heights: rowStates.map((state) => state.height),
    hunkReviewIds,
    moveEndpointIds,
    rowStates,
    virtualize: rowStates.length > 320,
  };
}

interface HunkProps {
  file: FileDiff;
  hunk: HunkModel;
  onFollowMove: FollowMove;
  part: "code" | "gutter";
  prepared: PreparedHunk;
  side: DiffSide;
}

function PaneHunk({ file, hunk, onFollowMove, part, prepared, side }: HunkProps) {
  const { collapsed, githubRangeKey, heights, hunkReviewIds, moveEndpointIds, rowStates, virtualize } = prepared;
  const virtual = useVirtualRows(heights, virtualize);
  const visibleRows = rowStates.slice(virtual.start, virtual.end);
  const moveAnchors = virtual.enabled && part === "code" ? rowStates.map((state, index) => {
    if (index >= virtual.start && index < virtual.end) return null;
    const line = side === "old" ? state.row.oldLine : state.row.newLine;
    const isMoveSide = line && (
      (side === "old" && line.moveRole === "source") ||
      (side === "new" && line.moveRole === "target")
    );
    if (!isMoveSide || !moveEndpointIds.has(line.id)) return null;
    return (
      <span
        class="virtual-move-anchor"
        data-move-id={line.moveId ?? undefined}
        id={line.id}
        key={`anchor:${line.id}`}
        style={{ top: `${virtual.offsets[index] + state.lineOffset}px` }}
      />
    );
  }) : null;
  const rows = visibleRows.map((state, visibleIndex) => {
    const {
      collapsedCount,
      hidden,
      moveCollapsed,
      reviewHidden,
      reviewId,
      row,
      skipCollapsed,
      startsGroup,
    } = state;
    const { alignedEdit, line, newLine, oldLine } = row;
    const rowIndex = virtual.start + visibleIndex;
    if (skipCollapsed || collapsed) return null;
    if (moveCollapsed) {
      return part === "code" && side === "new"
        ? <MoveCollapsePlaceholder count={collapsedCount} moveId={line.moveId!} key={`collapsed:${line.id}`} />
        : <div class="move-collapse-placeholder" key={`collapsed:${line.id}`} />;
    }
    return (
      <Fragment key={`${oldLine?.id ?? ""}:${newLine?.id ?? ""}:${rowIndex}`}>
        <MoveSpaces line={line} position="before" />
        {startsGroup && reviewHidden && (part === "code" && side === "new"
          ? <ReviewPlaceholder reviewId={reviewId!} />
          : <div class="review-placeholder" />)}
        <PaneDiffLine
          alignedEdit={alignedEdit}
          file={file}
          githubRangeKey={githubRangeKey}
          hidden={hidden}
          line={line}
          newLine={newLine}
          onFollowMove={onFollowMove}
          oldLine={oldLine}
          part={part}
          reviewId={reviewId}
          side={side}
          startsGroup={startsGroup}
        />
        <MoveSpaces line={line} position="after" />
      </Fragment>
    );
  });
  return (
    <div class="pane-hunk">
      <div class="hunk-header">
        {part === "gutter" ? <span>@@</span> : (
          <>
            <code>{side === "old" ? "−" : "+"}{side === "old" ? hunk.oldStart : hunk.newStart},{side === "old" ? hunk.oldCount : hunk.newCount}</code>
            {hunk.heading && <em title={safePlainText(hunk.heading)}><SafeText text={hunk.heading} /></em>}
            {side === "new" && hunkReviewIds.length > 0 && <HunkReviewButton reviewIds={hunkReviewIds} />}
          </>
        )}
      </div>
      {collapsed && (part === "code" && side === "new"
        ? <HunkPlaceholder reviewIds={hunkReviewIds} />
        : <div class="hunk-review-placeholder" />)}
      <div
        class={`virtual-hunk-rows${virtual.enabled ? " is-windowed" : ""}`}
        ref={virtual.containerRef}
        style={virtual.enabled ? { height: `${virtual.total}px` } : undefined}
      >
        {moveAnchors}
        {virtual.enabled ? (
          <div class="virtual-hunk-window" style={{ transform: `translateY(${virtual.top}px)` }}>{rows}</div>
        ) : rows}
      </div>
    </div>
  );
}

export function DiffFiles({ onFollowMove }: { onFollowMove: FollowMove }) {
  const { analysis, comparisons, hideUnchangedMoves, reviewed, showReviewed } = useApp();
  const moveEndpointIds = new Set(analysis.moves.flatMap((move) => [
    move.source.lineIds[0],
    move.source.lineIds.at(-1),
    move.target.lineIds[0],
    move.target.lineIds.at(-1),
  ].filter((id): id is string => Boolean(id))));
  return analysis.files.map((file, fileIndex) => {
    const paneColumns = file.hunks.reduce((fileMaximum, hunk) =>
      hunk.lines.reduce((hunkMaximum, line) => {
        return Math.max(hunkMaximum, visualColumns(line.text) + 2);
      }, fileMaximum), 40);
    const preparedHunks = file.hunks.map((hunk, hunkIndex) => prepareHunk(
      analysis,
      comparisons,
      fileIndex,
      hideUnchangedMoves,
      hunk,
      hunkIndex,
      moveEndpointIds,
      reviewed,
      showReviewed,
    ));
    return (
    <section
      class="file-card"
      id={`file-${fileIndex}`}
      data-file-index={fileIndex}
      key={fileIndex}
    >
      <FileHeader file={file} />
      {securityWarningCount(file.textSecurity) > 0 && <UnicodeWarning security={file.textSecurity} />}
      {file.isBinary && <p class="binary-message">Binary file changed</p>}
      {!file.isBinary && (
        <div class="file-panes">
          {(["old", "new"] as const).map((side) => (
            <div
              class={`file-pane file-pane-${side}`}
              key={side}
            >
              <div class="file-pane-gutter">
                <div class="file-pane-gutter-content">
                  {file.hunks.map((hunk, hunkIndex) => (
                    <PaneHunk
                      file={file}
                      hunk={hunk}
                      key={hunkIndex}
                      onFollowMove={onFollowMove}
                      part="gutter"
                      prepared={preparedHunks[hunkIndex]}
                      side={side}
                    />
                  ))}
                </div>
              </div>
              <div
                class="file-pane-scroll"
                role="region"
                aria-label={`Scroll ${side} file code horizontally`}
                data-pane-side={side}
                tabIndex={0}
              >
                <div
                  class="file-pane-content"
                  style={{ "--pane-code-width": `${paneColumns}ch` }}
                >
                  {file.hunks.map((hunk, hunkIndex) => (
                    <PaneHunk
                      file={file}
                      hunk={hunk}
                      key={hunkIndex}
                      onFollowMove={onFollowMove}
                      part="code"
                      prepared={preparedHunks[hunkIndex]}
                      side={side}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
    );
  });
}
